import React from 'react'
import { Message } from '@paypalcorp/worldready-react'

import useBreakpoint from '../lib/useBreakpoint'
import LogoImage from '../logo-image/LogoImage'
import defaultMerchantLogo from '../assets/default-store-logo.svg'
import { StoreDataType } from '../types/StorePageType'
import useWorldReady from '../lib/useWorldReady'

interface Props {
  data: StoreDataType
}

const StoreDetails = ({ data }: Props) => {
  const { isDeskTop, isLargeScreen } = useBreakpoint()
  const { formatMessage } = useWorldReady()

  if (!data) {
    return null
  }
  const {
    bestHoneyCoupons,
    averageSavings,
    availableOffers,
    merchantLogo,
    merchantLabel,
    couponCodes
  } = data

  const averageSavingsInDollars = averageSavings
    ? `$${(Number(averageSavings) / 100).toFixed(2)}`
    : 0

  const displayBestDiscount = bestHoneyCoupons ? (
    <tr data-cy="best_discount">
      <td className="store-details--grey">
        <Message id="main.storePage.details.bestDiscount.text" />
      </td>
      <td className="store-details--right">
        <Message
          id="main.storePage.details.bestDiscount.off"
          bestDiscount={bestHoneyCoupons}
        />
      </td>
    </tr>
  ) : null

  return (
    <div className="store-details" data-cy="storeDetails">
      <div className="store-details__content" data-cy="store-details__content">
        {(isDeskTop || isLargeScreen) && (
          <LogoImage
            src={merchantLogo}
            defaultSrc={defaultMerchantLogo}
            alt={merchantLabel}
            className="store-details__logo-image"
          />
        )}
        <AverageSavings averageSavingsInDollars={averageSavingsInDollars} />
        <table className="store-details__table">
          <tbody>
            <StoreDetailsBody
              dataCy="codes"
              isVisible={couponCodes !== 0}
              title={formatMessage('main.storePage.details.codes')}
              quantity={couponCodes}
            />
            <StoreDetailsBody
              dataCy="codes_and_deals"
              isVisible={availableOffers !== 0}
              title={formatMessage('main.storePage.details.codesAndDeals')}
              quantity={availableOffers}
            />
            <StoreDetailsBody
              dataCy="average_savings_body"
              isVisible={averageSavingsInDollars !== 0}
              title={formatMessage('main.storePage.details.averageSavings')}
              quantity={averageSavingsInDollars}
            />
            {displayBestDiscount}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface storeDetailsBodyProps {
  dataCy?: string
  isVisible: boolean
  title: string
  quantity: number | string
}

const StoreDetailsBody = ({
  dataCy,
  isVisible,
  title,
  quantity
}: storeDetailsBodyProps) => {
  if (!isVisible) {
    return null
  }
  return (
    <tr data-cy={dataCy}>
      <td className="store-details--grey">{title}</td>
      <td className="store-details--right">{quantity}</td>
    </tr>
  )
}

interface averageSavingsProps {
  averageSavingsInDollars: string | number
}

const AverageSavings = ({ averageSavingsInDollars }: averageSavingsProps) => {
  if (!averageSavingsInDollars) {
    return null
  }
  return (
    <>
      <div className="store-details--grey">
        <Message id="main.storePage.details.averageSavings" />
      </div>
      <div data-cy="average_savings" className="store-details__saving">
        {averageSavingsInDollars}
      </div>
      <div className="store-details--grey">
        <Message id="main.storePage.details.info" />
      </div>
    </>
  )
}

export default StoreDetails
